import { posApi } from '../api/endpoints';
import { ApiClientError } from '../api/client';
import { currentPerson } from '../auth/currentPerson';
import type { PosStore } from './store';
import type { RegisterMeta, SyncApi } from './types';

const REVOKED_MESSAGE =
  'Dieses Gerät wurde abgekoppelt oder gesperrt. Ihre Verkäufe bleiben gespeichert.';

/** Bei einer gekoppelten Kasse (Anmeldung) bedeutet 404 am Sync: abgekoppelt oder gesperrt. */
function asRevoked(caught: unknown, meta: RegisterMeta | undefined): unknown {
  if (meta?.stationId !== undefined && caught instanceof ApiClientError && caught.status === 404) {
    return new ApiClientError(404, 'DEVICE_REVOKED', REVOKED_MESSAGE);
  }
  return caught;
}

const FOREIGN_MESSAGE =
  'Diese Kasse gehört einer anderen Person. Ihre Verkäufe gehen unter deren Konto hinaus, sobald sie sich anmeldet.';

/**
 * Schutz vor der falschen Person: Mit dem Zugriffstoken einer anderen Person als der Besitzerin der Kasse geht nichts hinaus
 * (der Server würde mit 404 ablehnen, was die Kasse fälschlich als „abgekoppelt“ lesen würde). Ohne Anfrage und ohne Kassierer-
 * Verwechslung. Unbekannte Person (Start ohne Netz) und Schnellzugang sind nicht betroffen.
 */
function assertOwnPerson(meta: RegisterMeta | undefined): void {
  if (meta === undefined || meta.mode === 'name' || meta.ownerUserId === undefined) return;
  const person = currentPerson();
  if (person !== null && person !== meta.ownerUserId) {
    throw new ApiClientError(0, 'FOREIGN_REGISTER', FOREIGN_MESSAGE);
  }
}

/**
 * Die Anbindung der Kasse an den Server für Sync und Snapshot, je nach Kopplung: Schnellzugang (Geräte-Token, kein
 * Zugriffstoken) oder Anmeldung/freie Eröffnung (Basar-Sitzung). Es gibt sie einmal, für die Kassenseite (`usePos`) und für
 * den Hintergrund-Sender (`backgroundSend`). Das Protokoll ist unverändert. `sessionOnly` (Hintergrund-Sender): Sync nur mit der
 * Basar-Sitzung der Kasse, ohne Zugriffstoken; ohne Sitzung wird nichts gesendet.
 */
export function createSyncApi(
  eventId: string,
  store: PosStore,
  options: { sessionOnly?: boolean } = {},
): SyncApi {
  return {
    sync: async (registerId, body) => {
      const meta = await store.register();
      if (meta?.mode === 'name') {
        return posApi.syncDevice(eventId, registerId, body, meta.sessionToken ?? '');
      }
      // Hintergrund-Sender: nur mit der Sitzung der Kasse, nie mit dem Zugriffstoken der gerade angemeldeten Person
      if (options.sessionOnly === true && meta?.sessionToken === undefined) {
        throw new ApiClientError(401, 'UNAUTHORIZED', 'Die Kasse hat keine gültige Sitzung.');
      }
      if (options.sessionOnly !== true) assertOwnPerson(meta);
      try {
        return await (options.sessionOnly === true && meta?.sessionToken !== undefined
          ? posApi.syncWithSession(eventId, registerId, body, meta.sessionToken)
          : posApi.sync(eventId, registerId, body, meta?.sessionToken));
      } catch (caught) {
        throw asRevoked(caught, meta);
      }
    },
    uploadPhoto: async (registerId, photoId, data) => {
      const meta = await store.register();
      if (meta?.mode === 'name') {
        return posApi.uploadPhoto(eventId, registerId, photoId, data, {
          deviceToken: meta.sessionToken ?? '',
        });
      }
      if (options.sessionOnly === true && meta?.sessionToken === undefined) {
        throw new ApiClientError(401, 'UNAUTHORIZED', 'Die Kasse hat keine gültige Sitzung.');
      }
      if (options.sessionOnly !== true) assertOwnPerson(meta);
      return posApi.uploadPhoto(eventId, registerId, photoId, data, {
        ...(meta?.sessionToken === undefined ? {} : { sessionToken: meta.sessionToken }),
        ...(options.sessionOnly === true ? { sessionOnly: true } : {}),
      });
    },
    snapshot: async (query) => {
      const meta = await store.register();
      if (meta?.mode === 'name') {
        return posApi.snapshotDevice(eventId, query, meta.sessionToken ?? '');
      }
      assertOwnPerson(meta);
      return posApi.snapshot(eventId, query, meta?.sessionToken);
    },
  };
}
